import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { renderAsync } from "docx-preview";
import SignaturePad from "signature_pad";
import useApi from "../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../common/ToastHandler";
import useSwal from "../../common/ReusableComponent/useSwal";
import AuthContext from "../../../utils/secure-route/AuthContext";
import { ToastContainer } from "react-toastify";
// Load PDF.js
const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://sironline.in/vendor/build/pdf.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://sironline.in/vendor/build/pdf.worker.js";
      resolve(window.pdfjsLib);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};


const DocumentPreview = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const [, setLoading] = useState(true);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [signaturesLoaded, setSignaturesLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [signatureForm, setSignatureForm] = useState({
    name: '',
    initials: '',
    date: new Date().toISOString().split('T')[0],
    text: '',
    companyStamp: ''
  });
  const { confirmDelete } = useSwal();

  const [selectedFont, setSelectedFont] = useState(0);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedInitialFont] = useState(0);
  const [selectedInitialColor] = useState('#000000');
  const [droppedSignatures, setDroppedSignatures] = useState([]);
  const [selectedSignatureId, setSelectedSignatureId] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [availableSignatures, setAvailableSignatures] = useState([]);
  const [createdSignatureId, setCreatedSignatureId] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState('typed');
  const [stampFile, setStampFile] = useState(null);
  const [signaturePad, setSignaturePad] = useState(null);
  const [signatureSize, setSignatureSize] = useState({ width: 120, height: 40 });
  const [appliedSignatures, setAppliedSignatures] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);
  const [isLocked, setIsLocked] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState(null);
  const [currentAssignmentId, setCurrentAssignmentId] = useState(assignmentId || id);
  const [, setCanModify] = useState(true);
  const [storeOwnerSigned, setStoreOwnerSigned] = useState(false);

  const { PostFormData, GetPaginatedData, PostWithId, getHost, Get, getAPI, Delete, Post } = useApi();
  const { handleToast } = useToastHandler();
  const { user, permissions } = useContext(AuthContext);
  const userRole = permissions?.role_name || user?.role_name;
  const documentUrl = location.state?.documentUrl;
  const assignmentId = location.state?.assignmentId;
  const token = new URLSearchParams(location.search).get("token");

  const resolveFileUrl = (filePath) => {
    if (!filePath) return null;
    if (/^https?:\/\//i.test(filePath)) return filePath;
    const host = getHost().replace(/\/$/, "");
    const normalizedPath = String(filePath).replace(/^\/+/, "");
    const mediaPath = normalizedPath.startsWith("media/")
      ? normalizedPath
      : `media/${normalizedPath}`;
    return `${host}/${mediaPath}`;
  };

  const unwrapApiData = (response) => {
    let current = response?.data || response;
    const seen = new Set();

    while (
      current &&
      typeof current === "object" &&
      !Array.isArray(current) &&
      !seen.has(current)
    ) {
      seen.add(current);
      if (current.data && typeof current.data === "object") {
        current = current.data;
      } else if (current.result && typeof current.result === "object") {
        current = current.result;
      } else if (current.results && !Array.isArray(current.results) && typeof current.results === "object") {
        current = current.results;
      } else {
        break;
      }
    }

    return current;
  };

  const getDocumentFileFromAssignment = (assignment) => {
    const data = unwrapApiData(assignment);
    const candidates = [
      data?.document?.document_file,
      data?.document?.document_file_url,
      data?.document?.document_url,
      data?.document?.file_url,
      data?.document?.file,
      data?.document?.url,
      data?.document_file,
      data?.document_file_url,
      data?.document_url,
      data?.file_url,
      data?.file,
      data?.url,
      data?.document_path,
      data?.signed_document_relpath,
      data?.signed_document_url,
    ];

    return candidates.find(Boolean) || null;
  };

  const getDocumentIdFromAssignment = (assignment) => {
    const data = unwrapApiData(assignment);
    if (data?.document && typeof data.document === "string") return data.document;
    return data?.document?.id || data?.document_id || data?.document_uuid || null;
  };

  const buildApiUrl = (apiName, suffix = "") => `${getAPI(apiName)}${suffix}`;

  const generateId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  useEffect(() => {
    const callSignDocumentAPI = async () => {
      const assignmentData = location.state?.assignmentData;
      if (!assignmentData?.signing_token) return;
      if (userRole === 'store owner') {
        return;
      }

      console.log('Making API call for role:', userRole);
      try {
        const apiUrl = `${getAPI("document-assignment")}sign_document/?token=${assignmentData.signing_token}`;
        const response = await Get(apiUrl);
      } catch (error) {
        console.error("Error calling sign_document API:", error);
      }
    };
    callSignDocumentAPI();
  }, [location.state?.assignmentData, userRole]);


  const handleDeleteSavedSignature = async (signatureId) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      await Delete("digitalsignature", signatureId);
      handleToast({
        type: "SUCCESS",
        message: "Signature deleted successfully"
      });
      setRefreshKey(prev => prev + 1);
      // setAvailableSignatures(response.data || []);
    } catch (error) {
      console.error("Error deleting signature:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to delete signature"
      });
    }
  };


  useEffect(() => {
    const fetchSignatures = async () => {
      try {
        const response = await GetPaginatedData('digitalsignature');
        setAvailableSignatures(response.data || []);
        setSignaturesLoaded(true);
      } catch (error) {
        console.error('Error fetching signatures:', error);
        setSignaturesLoaded(true);
      }
    };
    fetchSignatures();
  }, [refreshKey]);


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    const loadDocument = async () => {
      try {
        let finalDocumentUrl = documentUrl;
        let resolvedAssignmentId = assignmentId || id;

        if (token) {
          const tokenResponse = unwrapApiData(
            await Get(buildApiUrl("document-assignment", `sign_document/?token=${encodeURIComponent(token)}`))
          );
          resolvedAssignmentId = tokenResponse?.id || resolvedAssignmentId;
          setCurrentAssignmentId(resolvedAssignmentId);
          const documentFile = getDocumentFileFromAssignment(tokenResponse);
          if (documentFile) {
            finalDocumentUrl = resolveFileUrl(documentFile);
          } else {
            const documentId = getDocumentIdFromAssignment(tokenResponse);
            if (documentId) {
              try {
                const documentResponse = unwrapApiData(
                  await Get(buildApiUrl("document", `${documentId}/?token=${encodeURIComponent(token)}`))
                );
                const directDocumentFile = getDocumentFileFromAssignment(documentResponse);
                if (directDocumentFile) {
                  finalDocumentUrl = resolveFileUrl(directDocumentFile);
                }
              } catch (error) {
                console.error("Error fetching document from token document id:", error);
              }
            }
          }
        }

        if (resolvedAssignmentId && !finalDocumentUrl) {
          try {
            const assignmentPath = token
              ? buildApiUrl("document-assignment", `${resolvedAssignmentId}/?token=${encodeURIComponent(token)}`)
              : buildApiUrl("document-assignment", `${resolvedAssignmentId}/`);
            const assignmentResponse = unwrapApiData(await Get(assignmentPath));
            const documentFile = getDocumentFileFromAssignment(assignmentResponse);
            if (documentFile) {
              finalDocumentUrl = resolveFileUrl(documentFile);
            }
          } catch (error) {
            console.error("Error fetching assignment document:", error);
          }
        }
        setCurrentAssignmentId(resolvedAssignmentId);
        if (resolvedAssignmentId && !token) {
          try {
            const apiUrl = `${getAPI("document-assignment")}${resolvedAssignmentId}/apply_signature/`;
            const response = await Get(apiUrl);
            setIsLocked(response.is_locked || false);
            setAssignmentStatus(response.assignment_status);
            setCanModify(response.can_modify !== false);
            setStoreOwnerSigned(response.store_owner_signed || false);
            if (response && response.signatures && response.signatures.length > 0) {
              const hasSignedDocument = response.signatures.some(sig => sig.signed_document_relpath);
              if (hasSignedDocument) {
                const signedDocSig = response.signatures.find(sig => sig.signed_document_relpath);
                finalDocumentUrl = resolveFileUrl(signedDocSig.signed_document_relpath);
              } else {
                setTimeout(() => {
                  const signatures = response.signatures.map(sig => {
                    if (!sig || !sig.signature) return null;
                    const imageUrl = sig.signature.signature_image ? resolveFileUrl(sig.signature.signature_image) : null;
                    return {
                      id: sig.id,
                      x: sig.signature_position_x,
                      y: sig.signature_position_y,
                      width: sig.signature_width,
                      height: sig.signature_height,
                      page: sig.signature_page,
                      text: sig.signature.signature_text || sig.signature.full_name,
                      color: sig.signature.color,
                      imageUrl: imageUrl,
                      type: 'applied'
                    };
                  }).filter(Boolean);
                  setAppliedSignatures(signatures);
                }, 1000);
              }
            }
          } catch (error) {
            console.error('Error fetching signatures:', error);
          }
        }

        if (!finalDocumentUrl) {
          setError("No document URL provided");
          setDocumentLoaded(true);
          return;
        }
        const fileExtension = finalDocumentUrl
          .split('?')[0]
          .split('.')
          .pop()
          .toLowerCase();
        setFileType(fileExtension);
        if (fileExtension === "pdf") {
          await loadPdfDocument(finalDocumentUrl);
          setDocumentLoaded(true);
          return;
        }
        if (fileExtension === "docx" || fileExtension === "doc") {
          const response = await fetch(finalDocumentUrl);
          if (!response.ok) {
            throw new Error("Failed to fetch DOCX");
          }
          const arrayBuffer = await response.arrayBuffer();
          if (previewRef.current) {
            previewRef.current.innerHTML = "";
            await renderAsync(arrayBuffer, previewRef.current);
          }
        }
        setDocumentLoaded(true);
      } catch (err) {
        setError(err.message);
        setDocumentLoaded(true);
      }
    };
    loadDocument();
  }, [documentUrl, assignmentId, id, token]);


  const signatureFonts = [
    { name: 'Signature 1', style: { fontFamily: 'Brush Script MT, cursive', fontStyle: 'italic', fontWeight: '400' } },
    { name: 'Signature 2', style: { fontFamily: 'Lucida Handwriting, cursive', fontStyle: 'normal', fontWeight: '400' } },
    { name: 'Signature 3', style: { fontFamily: 'Edwardian Script ITC,', fontStyle: 'italic', fontWeight: '400' } },
    { name: 'Signature 4', style: { fontFamily: 'Comic Sans MS, cursive', fontStyle: 'normal', fontWeight: 'bold' } },
    { name: 'Signature 5', style: { fontFamily: 'Bradley Hand, cursive', fontStyle: 'normal', fontWeight: 'bold' } }
  ];


  const colors = ['#000000', '#0066cc', '#cc0000', '#00cc00', '#ff6600'];
  useEffect(() => {
    if (documentLoaded && signaturesLoaded) {
      setLoading(false);
    }
  }, [documentLoaded, signaturesLoaded]);

  const handleDragStart = (e, type) => {
    const signatureData = {
      type,
      text: type === 'signature' ? (signatureForm.name || 'Signature') : (signatureForm.initials || 'AB'),
      font: type === 'signature' ? selectedFont : selectedInitialFont,
      color: type === 'signature' ? selectedColor : selectedInitialColor,
    };
    e.dataTransfer.setData('text/plain', JSON.stringify(signatureData));
  };



  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragItem) return;
      const container = previewRef.current || document.querySelector('div[style*="position: relative"]');
      if (!container) {
        return;
      }
      const rect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop || 0;
      const x = Math.max(
        0,
        Math.min(e.clientX - rect.left - dragOffset.x, container.offsetWidth - signatureSize.width)
      );
      const y = Math.max(
        0,
        Math.min(e.clientY - rect.top - dragOffset.y + scrollTop, container.scrollHeight - signatureSize.height)
      );
      setDroppedSignatures((prev) => {
        const updated = prev.map((sig) =>
          sig.id === dragItem ? { ...sig, x, y } : sig
        );
        return updated;
      });
    };


    const handleMouseUp = () => {
      setDragItem(null);
    };
    if (dragItem) {
      window.addEventListener("mousemove", handleMouseMove);
    }
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragItem, dragOffset, signatureSize]);



  const handleDrop = (e) => {
    e.preventDefault();
    if (isLocked) return;
    const rawData = e.dataTransfer.getData('application/signature');
    if (!rawData) return;
    let signatureData;
    try {
      signatureData = JSON.parse(rawData);
    } catch (err) {
      console.error("Invalid drag data:", rawData);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollTop = e.currentTarget.scrollTop || 0;
    const baseX = e.clientX - rect.left - (signatureSize.width / 2);
    const baseY = e.clientY - rect.top + scrollTop - (signatureSize.height / 2);
    const maxX = rect.width - signatureSize.width;
    const maxY = e.currentTarget.scrollHeight - signatureSize.height;
    const newId = generateId();
    setDroppedSignatures(prev => {
      return [
        ...prev,
        {
          ...signatureData,
          id: newId,
          x: Math.max(0, Math.min(baseX, maxX)),
          y: Math.max(0, Math.min(baseY, maxY)),
          width: signatureSize.width,
          height: signatureSize.height,
          page: pageNum
        }
      ];
    });
    setSelectedSignatureId(newId);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleSignatureDragStart = (e, signatureId) => {
    e.preventDefault();
    const rect = e.target.getBoundingClientRect();
    const container = e.target.closest('.docx-preview-container') || e.target.closest('div[style*="position: relative"]');
    const containerRect = container ? container.getBoundingClientRect() : rect;
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setDragItem(signatureId);
  };
  const handleSignatureDrag = (e) => {
    if (!dragItem) return;
    e.preventDefault();
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const scrollThreshold = 50;
    const scrollSpeed = 10;
    if (e.clientY - rect.top < scrollThreshold && container.scrollTop > 0) {
      container.scrollTop -= scrollSpeed;
    } else if (rect.bottom - e.clientY < scrollThreshold) {
      container.scrollTop += scrollSpeed;
    }
    const x = Math.max(0, Math.min(e.clientX - rect.left - dragOffset.x, container.offsetWidth - 100));
    const y = Math.max(0, Math.min((e.clientY - rect.top + container.scrollTop) - dragOffset.y, container.scrollHeight - 50));
    setDroppedSignatures(prev =>
      prev.map(sig =>
        sig.id === dragItem ? { ...sig, x, y } : sig
      )
    );
  };
  const handleSignatureDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDragItem(null);
  };
  const clearSignatureForm = () => {
    setSignatureForm({
      name: '',
      initials: '',
      date: new Date().toISOString().split('T')[0],
      text: '',
      companyStamp: ''
    });
    setStampFile(null);
    setSelectedFont(0);
    setSelectedColor('#000000');
    if (signaturePad) {
      signaturePad.clear();
    }
    setSignaturePad(null);
  };

  const handleSignatureTypeChange = (newType) => {
    setSignatureType(newType);
    if (signaturePad) {
      signaturePad.clear();
    }
    setStampFile(null);
  };

  const handleCloseModal = () => {
    setShowSignatureModal(false);
    setSignatureType('typed');
    clearSignatureForm();
  };
  const isCanvasEmpty = () => {
    return signaturePad ? signaturePad.isEmpty() : true;
  };
  const createDigitalSignature = async () => {
    if (!signatureForm.name || !signatureForm.initials) {
      handleToast({
        type: "ERROR",
        message: "Please enter both full name and initials"
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('full_name', signatureForm.name);
      formData.append('initials', signatureForm.initials);
      formData.append('signature_type', signatureType);
      formData.append('color', selectedColor);
      if (signatureType === 'draw' && signaturePad) {
        const dataURL = signaturePad.toDataURL('image/png');
        const blob = await fetch(dataURL).then(r => r.blob());
        formData.append('signature_image', blob, 'signature.png');

      } else if (signatureType === 'stamp' && stampFile) {
        formData.append('signature_image', stampFile);
      } else {
        formData.append('signature_text', signatureForm.name.toUpperCase());
      }
      const response = await PostFormData('digitalsignature', formData);
      setCreatedSignatureId(response.data.id);
      const sigResponse = await GetPaginatedData('digitalsignature');
      setAvailableSignatures(sigResponse.data || []);
      setShowSignatureModal(false);
      handleToast({
        type: "SUCCESS",
        message: "Digital signature created successfully!"
      });
    } catch (error) {
      console.error('Error creating digital signature:', error);
      handleToast({
        type: "ERROR",
        message: "Error creating digital signature"
      });
    }
  };
  const saveDrawnSignature = () => {
    if (!signaturePad || isCanvasEmpty()) {
      handleToast({
        type: "ERROR",
        message: "Please draw a signature"
      });
      return;
    }
    createDigitalSignature();
  };
  const renderPdfPage = async (pdf, pageNumber) => {
    if (!canvasRef.current) return;

    const page = await pdf.getPage(pageNumber);
    const scale = isMobile ? 1.0 : 1.25;

    const viewport = page.getViewport({ scale });
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({
      canvasContext: context,
      viewport
    }).promise;
  };


  const loadPdfDocument = async (url) => {
    try {
      const pdfjsLib = await loadPdfJs();
      const pdf = await pdfjsLib.getDocument({
        url,
        withCredentials: false
      }).promise;

      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setPageNum(1);

      await renderPdfPage(pdf, 1);
    } catch (err) {
      console.error(err);
      setError("Unable to load PDF");
    }
  };

  const goToPage = async (newPageNum) => {
    if (!pdfDoc || newPageNum < 1 || newPageNum > totalPages) return;

    setPageNum(newPageNum);
    await renderPdfPage(pdfDoc, newPageNum);
  };


  const removeSignature = (signatureId) => {
    setDroppedSignatures(prev => prev.filter(sig => sig.id !== signatureId));
    if (selectedSignatureId === signatureId) {
      setSelectedSignatureId(null);
    }
  };

  const updateSignatureSize = (signatureId, dimension, value) => {
    setDroppedSignatures(prev =>
      prev.map(sig =>
        sig.id === signatureId
          ? { ...sig, [dimension]: parseInt(value) }
          : sig
      )
    );
  };

  const submitSignature = async () => {
    if (isLocked) {
      handleToast({
        type: "ERROR",
        message: "Document is locked. Signatures cannot be modified."
      });
      return;
    }
    if (droppedSignatures.length === 0) {
      handleToast({
        type: "ERROR",
        message: "Please drag at least one signature to the document"
      });
      return;
    }

    try {
      for (const signature of droppedSignatures) {
        const payload = {
          signature_id:
            signature.signatureId ||
            availableSignatures[0]?.id ||
            createdSignatureId,
          position_x: signature.x,
          position_y: signature.y,
          signature_width: signature.width || signatureSize.width,
          signature_height: signature.height || signatureSize.height,
          page: signature.page || pageNum
        };

        const targetAssignmentId = currentAssignmentId || assignmentId || id;
        await PostWithId(
          "document-assignment",
          `${targetAssignmentId}/apply_signature/`,
          payload
        );
      }

      setAppliedSignatures(prev => [
        ...prev,
        ...droppedSignatures.map(sig => ({ ...sig, type: "applied" }))
      ]);
      setDroppedSignatures([]);

      handleToast({
        type: "SUCCESS",
        message: "Signatures applied successfully!"
      });

      setTimeout(() => navigate(-1), 1500);

    } catch (error) {
      console.error("Error submitting signature:", error);
      if (error.response?.status === 403) {
        handleToast({
          type: "ERROR",
          message: "Document is locked. Signature cannot be modified."
        });
      } else {
        handleToast({
          type: "ERROR",
          message: "Error submitting signature"
        });
      }
    }
  };


  if (error) {
    return (
      <div className="responsive-box">
        <div className="header-row">
          <div style={{
            // minHeight: '100vh', 
            // backgroundColor: '#f1f5f9',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            <div >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '12px' : '16px',
                maxWidth: '1400px',
                margin: '0 auto'
              }}>
                <button
                  onClick={() => navigate(-1)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px',
                    backgroundColor: '#475569',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '500'
                  }}
                >
                  ← Back
                </button>
                <h1 style={{
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: '600',
                  color: '#1e293b',
                  margin: 0
                }}>Document Preview</h1>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            maxWidth: '720px',
            margin: '72px auto',
            padding: isMobile ? '24px' : '32px',
            borderRadius: '18px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: '#fee2e2',
              color: '#dc2626',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 700,
              marginBottom: '18px'
            }}
          >
            !
          </div>
          <h2 style={{ margin: '0 0 10px', color: '#111827', fontSize: isMobile ? '22px' : '28px' }}>
            Document preview could not be loaded
          </h2>
          <p style={{ margin: '0 0 24px', color: '#64748b', fontSize: '16px' }}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 22px',
              border: 0,
              borderRadius: '10px',
              background: '#1f2937',
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }



  return (
    <div >
      <ToastContainer />
      {/* Header */}
      <div style={{
        marginTop: "20px",
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
            <button
              onClick={() => navigate(-1)}
              style={{

                padding: isMobile ? '6px 10px' : '8px 12px',
                backgroundColor: '#475569',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(71, 85, 105, 0.2)'
              }}

            >
              ← Back
            </button>
            <h1 style={{
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: '600',
              color: '#1e293b',
              margin: 0
            }}>Document Preview & Signature</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: isTablet ? 'column' : 'row',
        gap: isMobile ? '16px' : '20px',
        padding: isMobile ? '16px' : '20px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Document Viewer */}
        <div style={{
          flex: '1',
          minWidth: '0',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          {/* Document Header */}
          <div style={{
            padding: isMobile ? '12px 16px' : '16px 20px',
            borderBottom: '1px solid #f1f5f9',
            backgroundColor: '#fafbfc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>📄</span>
              <span style={{
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                color: '#374151'
              }}>Document Viewer</span>
            </div>
            {fileType === 'pdf' && totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                <button
                  onClick={() => goToPage(pageNum - 1)}
                  disabled={pageNum <= 1}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: pageNum <= 1 ? '#f3f4f6' : '#3b82f6',
                    color: pageNum <= 1 ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pageNum <= 1 ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontWeight: '600' }}>Page {pageNum} of {totalPages}</span>
                <button
                  onClick={() => goToPage(pageNum + 1)}
                  disabled={pageNum >= totalPages}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: pageNum >= totalPages ? '#f3f4f6' : '#3b82f6',
                    color: pageNum >= totalPages ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pageNum >= totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>

          {/* Signature Position Notice */}
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#fef3c7',
            borderBottom: '1px solid #f59e0b',
            fontSize: '12px',
            color: '#92400e',
            textAlign: 'center'
          }}>
            ⚠️ Note: Signature positions might not display correctly in this preview
          </div>
          {fileType === 'pdf' ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: isMobile ? '60vh' : '80vh',
                minHeight: isMobile ? '400px' : '500px',
                overflow: 'auto',
                // backgroundColor: '#f8fafc',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '20px'
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onMouseMove={handleSignatureDrag}
            >
              <canvas
                ref={canvasRef}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto',
                  // boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  borderRadius: '8px',
                  // backgroundColor: 'white'
                }}
              />
              {[...droppedSignatures, ...appliedSignatures].filter(Boolean).filter(signature => !signature.page || signature.page === pageNum).map((signature, index) => (
                <div
                  key={signature.id}
                  onClick={() => signature.type !== 'applied' && !isLocked && setSelectedSignatureId(signature.id)}
                  onMouseDown={(e) => signature.type !== 'applied' && !isLocked && handleSignatureDragStart(e, signature.id)}
                  onDoubleClick={() => signature.type !== 'applied' && !isLocked && removeSignature(signature.id)}
                  style={{
                    position: 'absolute',
                    left: signature.x,
                    top: signature.y,
                    zIndex: 100 + index,
                    cursor: signature.type === 'applied' ? 'default' : 'move',
                    userSelect: 'none',
                    padding: 0,
                    border: signature.type === 'applied' ? 'none' : (selectedSignatureId === signature.id ? '2px solid #f5f5f5' : 'none'),
                    borderRadius: '2px',
                    boxShadow: signature.type === 'applied' ? 'none' : (selectedSignatureId === signature.id ? '0 4px 12px rgba(59,130,246,0.3)' : 'none'),
                    backgroundColor: 'transparent'
                  }}
                  title={signature.type === 'applied' ? '✅ Applied Signature' : '🖱️ Click to select • ✋ Drag to move • ❌ Double-click to remove'}
                >
                  {signature.imageUrl ? (
                    <img
                      src={signature.imageUrl}
                      alt="Signature"
                      style={{
                        width: `${signature.width}px`,
                        height: `${signature.height}px`,
                        objectFit: 'contain',
                        backgroundColor: 'transparent',
                        mixBlendMode: 'multiply',
                        pointerEvents: 'none'
                      }}
                    />
                  ) : (
                    <div style={{
                      ...signatureFonts[signature.font || 0].style,
                      color: signature.color,
                      fontSize: signature.type === 'signature' ? '24px' : '18px'
                    }}>
                      {signature.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div
                ref={previewRef}
                style={{
                  width: '100%',
                  minHeight: isMobile ? '60vh' : '70vh',
                  padding: isMobile ? '16px' : '20px',
                  position: 'relative',
                  cursor: dragItem ? 'grabbing' : 'default',
                  overflow: 'auto',
                  backgroundColor: '#fafbfc'
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onMouseMove={handleSignatureDrag}
              >
                {[...droppedSignatures, ...appliedSignatures].filter(Boolean).map((signature, index) => (
                  <div
                    key={signature.id}
                    onClick={() => signature.type !== 'applied' && !isLocked && setSelectedSignatureId(signature.id)}
                    onMouseDown={(e) => signature.type !== 'applied' && !isLocked && handleSignatureDragStart(e, signature.id)}
                    onDoubleClick={() => signature.type !== 'applied' && !isLocked && removeSignature(signature.id)}
                    style={{
                      position: 'absolute',
                      left: signature.x,
                      top: signature.y,
                      cursor: signature.type === 'applied' ? 'default' : (dragItem === signature.id ? 'grabbing' : 'grab'),
                      zIndex: dragItem === signature.id ? 20 : 10,
                      userSelect: 'none',
                      padding: 0,
                      border: signature.type === 'applied' ? 'none' : (selectedSignatureId === signature.id ? '2px solid #3b82f6' : 'none'),
                      borderRadius: '2px',
                      backgroundColor: 'transparent',
                      boxShadow: signature.type === 'applied' ? 'none' : (selectedSignatureId === signature.id ? '0 4px 12px rgba(59,130,246,0.3)' : 'none'),
                      transform: dragItem === signature.id ? 'scale(1.05)' : 'scale(1)',
                      transition: dragItem === signature.id ? 'none' : 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (signature.type !== 'applied' && dragItem !== signature.id) {
                        e.target.style.border = '2px solid #3b82f6';
                        e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.05)';
                        e.target.style.transform = 'scale(1.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (signature.type !== 'applied' && dragItem !== signature.id) {
                        e.target.style.border = '1px dashed transparent';
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                    title={signature.type === 'applied' ? '✅ Applied Signature' : '🖱️ Click to select • ✋ Drag to move • ❌ Double-click to remove'}
                  >
                    {signature.imageUrl ? (
                      <img
                        src={signature.imageUrl}
                        alt="Signature"
                        style={{
                          width: `${signature.width}px`,
                          height: `${signature.height}px`,
                          objectFit: 'contain',
                          backgroundColor: 'transparent',
                          mixBlendMode: 'multiply'
                        }}
                      />
                    ) : (
                      <div style={{
                        ...signatureFonts[signature.font || 0].style,
                        color: signature.color,
                        fontSize: signature.type === 'signature' ? '24px' : '18px'
                      }}>
                        {signature.text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Signature Panel - Hide if document is completed or store owner signed */}
        {assignmentStatus !== 'completed' && !storeOwnerSigned && (
          <div style={{
            width: isTablet ? '100%' : '380px',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            padding: isMobile ? '16px' : '24px',
            height: 'fit-content',
            position: isTablet ? 'static' : 'sticky',
            top: isTablet ? 'auto' : '100px',
            border: '1px solid #e2e8f0'
          }}>
            {/* Header */}
            <div style={{
              marginBottom: '24px',
              textAlign: 'center',
              borderBottom: '2px solid #f1f5f9',
              paddingBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '24px' }}>{isLocked ? '🔒' : '✍️'}</span>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '700',
                  color: '#1e293b'
                }}>Signature Tools</h3>
              </div>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '12px' : '14px',
                color: isLocked ? '#dc2626' : '#64748b',
                fontWeight: '500'
              }}>
                {isLocked ? 'Document is locked - signatures cannot be modified' : 'Drag signatures to the document'}
              </p>
            </div>
            {/* Signature Size Controls */}
            <div style={{
              marginBottom: '24px',
              padding: isMobile ? '14px' : '18px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{
                fontSize: isMobile ? '13px' : '14px',
                color: '#374151',
                marginBottom: '16px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>📏</span> {selectedSignatureId ? 'Selected Signature Size' : 'Default Signature Size'}
              </div>
              {selectedSignatureId ? (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Width</label>
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                        {droppedSignatures.find(s => s.id === selectedSignatureId)?.width || signatureSize.width}px
                      </span>
                    </div>
                    <input
                      type="range"
                      min="60"
                      max="200"
                      value={droppedSignatures.find(s => s.id === selectedSignatureId)?.width || signatureSize.width}
                      onChange={(e) => !isLocked && updateSignatureSize(selectedSignatureId, 'width', e.target.value)}
                      disabled={isLocked}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: isLocked ? '#e5e7eb' : '#3b82f6',
                        outline: 'none',
                        cursor: isLocked ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Height</label>
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                        {droppedSignatures.find(s => s.id === selectedSignatureId)?.height || signatureSize.height}px
                      </span>
                    </div>
                    <input
                      type="range"
                      min="20"
                      max="100"
                      value={droppedSignatures.find(s => s.id === selectedSignatureId)?.height || signatureSize.height}
                      onChange={(e) => !isLocked && updateSignatureSize(selectedSignatureId, 'height', e.target.value)}
                      disabled={isLocked}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: isLocked ? '#e5e7eb' : '#3b82f6',
                        outline: 'none',
                        cursor: isLocked ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                  <button
                    onClick={() => setSelectedSignatureId(null)}
                    style={{
                      marginTop: '12px',
                      width: '100%',
                      padding: '8px',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    Clear Selection
                  </button>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Width</label>
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>{signatureSize.width}px</span>
                    </div>
                    <input
                      type="range"
                      min="60"
                      max="200"
                      value={signatureSize.width}
                      onChange={(e) => !isLocked && setSignatureSize(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                      disabled={isLocked}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: isLocked ? '#e5e7eb' : '#3b82f6',
                        outline: 'none',
                        cursor: isLocked ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Height</label>
                      <span style={{ fontSize: '12px', color: '#374151', fontWeight: '600' }}>{signatureSize.height}px</span>
                    </div>
                    <input
                      type="range"
                      min="20"
                      max="100"
                      value={signatureSize.height}
                      onChange={(e) => !isLocked && setSignatureSize(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                      disabled={isLocked}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: isLocked ? '#e5e7eb' : '#3b82f6',
                        outline: 'none',
                        cursor: isLocked ? 'not-allowed' : 'pointer'
                      }}
                    />
                  </div>
                  <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '6px',
                    fontSize: '11px',
                    color: '#1e40af',
                    textAlign: 'center'
                  }}>
                    💡 Click a signature to adjust its size
                  </div>
                </>
              )}
            </div>
            {/* Create New Signature Button */}
            <div style={{ marginBottom: '24px' }}>
              <button
                onClick={() => {
                  if (!isLocked) {
                    clearSignatureForm();
                    setShowSignatureModal(true);
                  }
                }}
                disabled={isLocked}
                style={{
                  width: '100%',
                  padding: isMobile ? '14px' : '16px',
                  backgroundColor: isLocked ? '#e5e7eb' : '#3b82f6',
                  color: isLocked ? '#9ca3af' : 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: isLocked ? 'none' : '0 4px 6px -1px rgba(59, 130, 246, 0.3)'
                }}
              >
                <span style={{ fontSize: '18px' }}>✍️</span>
                Create New Signature
              </button>
            </div>
            {/* Saved Signatures */}
            {availableSignatures.length > 0 && (
              <div style={{
                marginBottom: '24px',
                padding: isMobile ? '14px' : '18px',
                // backgroundColor: '#f8fafc',
                borderRadius: '12px',
                // border: '1px solid #e2e8f0'
              }}>
                <div style={{
                  fontSize: isMobile ? '13px' : '14px',
                  color: '#374151',
                  marginBottom: '16px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>📋</span> Saved Signatures
                </div>
                <div style={{
                  maxHeight: isMobile ? '150px' : '200px',
                  overflowY: 'auto',
                  gap: '10px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {availableSignatures.map((sig) => {

                    return (
                      <div
                        key={sig.id}
                        draggable={!isLocked}
                        onDragStart={(e) => {
                          if (isLocked) return;
                          setIsDragging(true);
                          const signatureData = {
                            type: 'saved',
                            text: sig.signature_text || sig.full_name,
                            font: 0,
                            color: sig.color || '#000000',
                            id: Date.now() + Math.random(),
                            signatureId: sig.id,
                            imageUrl: sig.signature_image ? resolveFileUrl(sig.signature_image) : null
                          };
                          e.dataTransfer.setData('application/signature', JSON.stringify(signatureData));
                          e.target.style.opacity = '0.6';
                        }}
                        onDragEnd={(e) => {
                          if (isLocked) return;
                          setIsDragging(false);
                          e.target.style.opacity = '1';
                        }}
                        style={{
                          padding: '12px',
                          // border: '2px dashed #d1d5db',
                          borderRadius: '8px',
                          cursor: isLocked ? 'not-allowed' : 'grab',
                          // backgroundColor: '#f9fafb',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          opacity: isLocked ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#3b82f6';
                          // e.target.style.backgroundColor = '#eff6ff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.backgroundColor = '#f9fafb';
                        }}
                      >
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {sig.signature_image ? (
                            <img
                              src={resolveFileUrl(sig.signature_image)}
                              alt="Signature"
                              style={{
                                height: '24px',
                                maxWidth: '60px',
                                objectFit: 'contain'
                              }}
                            />
                          ) : (
                            <div style={{
                              ...signatureFonts[0].style,
                              color: sig.color || '#000000',
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {sig.signature_text || sig.full_name}
                            </div>
                          )}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {!isDragging && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSavedSignature(sig.id);
                              }}
                              style={{
                                // background: '#fee2e2',
                                border: 'none',
                                borderRadius: '4px',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                cursor: 'pointer',
                                color: '#dc2626',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#fecaca';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#fee2e2';
                              }}
                              title="Delete signature"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{
                  marginTop: '12px',
                  padding: '8px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#92400e',
                  textAlign: 'center'
                }}>
                  👆 Drag signatures to the document
                </div>
              </div>
            )}
            {/* Apply Signatures Button */}
            <button
              onClick={isLocked ? undefined : submitSignature}
              disabled={isLocked || droppedSignatures.length === 0}
              style={{
                width: '100%',
                padding: isMobile ? '14px' : '16px',
                backgroundColor: (isLocked || droppedSignatures.length === 0) ? '#e5e7eb' : '#10b981',
                color: (isLocked || droppedSignatures.length === 0) ? '#9ca3af' : 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: (isLocked || droppedSignatures.length === 0) ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: (isLocked || droppedSignatures.length === 0) ? 'none' : '0 4px 6px -1px rgba(16, 185, 129, 0.3)'
              }}

            >
              <span style={{ fontSize: '16px' }}>✓</span>
              Apply Signatures
            </button>
          </div>
        )}
        {showSignatureModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              width: isMobile ? '100%' : '400px',
              maxWidth: '400px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151'
                }}>Create Signature</h3>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Form */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={signatureForm.name}
                    onChange={(e) => setSignatureForm({ ...signatureForm, name: e.target.value })}
                    style={{
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Initials *"
                    value={signatureForm.initials}
                    onChange={(e) => setSignatureForm({ ...signatureForm, initials: e.target.value })}
                    style={{
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>


                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px'
                  }}>
                    {[
                      { id: 'typed', label: 'Type', icon: '📝' },
                      { id: 'draw', label: 'Draw', icon: '✍️' },
                      { id: 'stamp', label: 'Upload', icon: '🏢' }
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => handleSignatureTypeChange(type.id)}
                        style={{
                          padding: '12px 8px',
                          border: signatureType === type.id ? '2px solid #3b82f6' : '1px solid #d1d5db',
                          borderRadius: '8px',
                          backgroundColor: signatureType === type.id ? '#eff6ff' : 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontSize: '16px', marginBottom: '4px' }}>{type.icon}</div>
                        <div style={{ fontWeight: '500' }}>{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>


                {signatureType === 'typed' && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '8px'
                    }}>
                      {signatureFonts.map((font, index) => (
                        <label key={index} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: selectedFont === index ? '#f3f4f6' : 'transparent'
                        }}>
                          <input
                            type="radio"
                            name="font"
                            checked={selectedFont === index}
                            onChange={() => setSelectedFont(index)}
                            style={{ margin: 0 }}
                          />
                          <div style={{
                            ...font.style,
                            fontSize: '14px',
                            color: selectedColor,
                            flex: 1
                          }}>
                            {signatureForm.name || 'Sample'}
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {signatureType === 'draw' && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e',
                      textAlign: 'center'
                    }}>
                      💡 Select a pen color first before drawing
                    </div>
                    <canvas
                      ref={(canvas) => {
                        if (canvas && signatureType === 'draw') {
                          if (!signaturePad) {
                            const pad = new SignaturePad(canvas, {
                              backgroundColor: 'transparent',
                              penColor: selectedColor
                            });
                            setSignaturePad(pad);
                          } else {
                            signaturePad.penColor = selectedColor;
                          }
                        }
                      }}
                      width={360}
                      height={120}
                      style={{
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        width: '100%',
                        backgroundColor: 'transparent'
                      }}
                    />
                    <button
                      onClick={() => signaturePad?.clear()}
                      style={{
                        marginTop: '8px',
                        padding: '6px 12px',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}

                {signatureType === 'stamp' && (
                  <div style={{ marginBottom: '16px' }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setStampFile(e.target.files[0])}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                )}


                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center'
                  }}>
                    {colors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedColor(color);
                          if (signaturePad && signatureType === 'draw') {
                            signaturePad.penColor = color;
                          }
                        }}
                        style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: color,
                          borderRadius: '50%',
                          border: selectedColor === color ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                          cursor: 'pointer'
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>


              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={handleCloseModal}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={signatureType === 'draw' ? saveDrawnSignature : createDigitalSignature}
                  disabled={!signatureForm.name || !signatureForm.initials || (signatureType === 'stamp' && !stampFile)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: (!signatureForm.name || !signatureForm.initials || (signatureType === 'stamp' && !stampFile)) ? '#d1d5db' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!signatureForm.name || !signatureForm.initials || (signatureType === 'stamp' && !stampFile)) ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
        <style>{`
          .docx-preview-container {
            background: transparent !important;
          }
          .docx-preview-container .docx-wrapper {
            font-family: 'Times New Roman', serif;
            line-height: 1.6;
            color: #333;
            background: white;
            padding: 0;
            margin: 0;
            box-shadow: none;
          }
          .docx-preview-container .docx-wrapper p {
            margin: 0 0 12px 0;
          }
          .docx-preview-container .docx-wrapper table {
            border-collapse: collapse;
            width: 100%;
            margin: 12px 0;
          }
          .docx-preview-container .docx-wrapper td, 
          .docx-preview-container .docx-wrapper th {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
        `}</style>
      </div>
    </div>
  );
};
export default DocumentPreview;
